/**
 * Result tables + assumption cards for a parsed financial model.
 *
 * Reads the `effective` payload from /api/portfolios/:pid/financial-models/:mid:
 *   • effective.scalars            → assumption cards
 *   • effective.period_grid        → table columns
 *   • effective.frequency          → header chip per group
 *   • effective.time_series_groups → one table per group, sticky label col
 *
 * Nothing is fabricated — values come straight from the workbook (with
 * overrides applied). Missing values render as "—" rather than 0.
 */

import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Stack, Chip, Card, CardContent, Button,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';

// ── Formatters by unit ─────────────────────────────────────────────────────

const fmtAud = (v) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a >= 1e9) return `A$${(v / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `A$${(v / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `A$${(v / 1e3).toFixed(0)}k`;
  return `A$${v.toFixed(0)}`;
};
const fmtMwh = (v) => (v == null ? '—' : `${Math.round(v).toLocaleString('en-AU')}`);
const fmtX = (v) => (v == null ? '—' : `${Number(v).toFixed(2)}x`);
const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const fmtAudPerMwh = (v) => (v == null ? '—' : `A$${Number(v).toFixed(2)}`);

const fmtByUnit = (v, unit) => {
  if (unit === 'AUD') return fmtAud(v);
  if (unit === 'MWh') return fmtMwh(v);
  if (unit === 'x')   return fmtX(v);
  if (unit === '%')   return fmtPct(v);
  if (unit === 'AUD/MWh') return fmtAudPerMwh(v);
  return v == null ? '—' : String(v);
};

// ── Assumption (scalars) card grid ─────────────────────────────────────────

const SCALAR_LAYOUT = [
  {
    key: 'yield', title: 'Yield',
    items: [
      ['p50_mwh', 'P50 (MWh/yr)', fmtMwh],
      ['p90_mwh', 'P90 (MWh/yr)', fmtMwh],
      ['p99_mwh', 'P99 (MWh/yr)', fmtMwh],
      ['degradation_pct_ongoing', 'Degradation (ongoing)', fmtPct],
      ['availability_factor_pct', 'Availability', fmtPct],
    ],
  },
  {
    key: 'capacity', title: 'Capacity',
    items: [
      ['ac_mw', 'AC (MW)', (v) => v == null ? '—' : `${v.toFixed(1)} MW`],
      ['nameplate_mw', 'Nameplate (MW)', (v) => v == null ? '—' : `${v.toFixed(1)} MW`],
    ],
  },
  {
    key: 'debt_pricing', title: 'Debt pricing',
    items: [
      ['senior_margin_pct', 'Senior margin', fmtPct],
      ['base_rate_pct', '3M BBSY', fmtPct],
      ['coupon_pct', 'All-in interest', fmtPct],
      ['max_debt_aud', 'Max debt', fmtAud],
      ['drawn_debt_aud', 'Drawn debt', fmtAud],
      ['commitment_fee_pct_of_margin', 'Commitment fee (% of margin)', fmtPct],
      ['upfront_fee_pct', 'Upfront fee', fmtPct],
      ['refinancing_fee_pct', 'Refinancing fee', fmtPct],
    ],
  },
  {
    key: 'covenants', title: 'Covenants & sizing',
    items: [
      ['lockup_dscr', 'Lock-up DSCR', fmtX],
      ['default_dscr', 'Default DSCR', fmtX],
      ['lookback_months', 'Lookback', (v) => v == null ? '—' : `${v}m`],
      ['dscr_sizing_p50_contracted', 'Sizing DSCR (P50 contracted)', fmtX],
      ['dscr_sizing_p50_uncontracted', 'Sizing DSCR (P50 uncontracted)', fmtX],
      ['llcr', 'LLCR', fmtX],
    ],
  },
  {
    key: 'reserves', title: 'Reserves',
    items: [
      ['min_cash_balance_aud', 'Min cash balance', fmtAud],
      ['cash_reserve_threshold_bau', 'CRA threshold (BAU)', (v) => v == null ? '—' : `${(v * 100).toFixed(0)}%`],
      ['dsra_look_forward_periods', 'DSRA look-forward', (v) => v == null ? '—' : `${v}p`],
      ['dsrf_look_forward_periods', 'DSRF look-forward', (v) => v == null ? '—' : `${v}p`],
    ],
  },
  {
    key: 'prices', title: 'Prices',
    items: [
      ['breakeven_price_real_aud_per_mwh', 'Breakeven price (Real)', fmtAudPerMwh],
    ],
  },
  {
    key: 'capex', title: 'CAPEX',
    items: [
      ['total_aud', 'Total CAPEX', fmtAud],
      ['solar_farm_aud', 'Solar farm', fmtAud],
      ['substation_aud', 'Substation & switchyard', fmtAud],
      ['access_road_aud', 'Access road', fmtAud],
      ['harmonic_filter_aud', 'Harmonic filter', fmtAud],
    ],
  },
  {
    key: 'opex', title: 'OPEX',
    items: [
      ['annual_om_real23_aud', 'Annual O&M (Real)', fmtAud],
    ],
  },
];

function AssumptionCards({ scalars }) {
  if (!scalars) return null;
  return (
    <Box sx={{
      display: 'grid', gap: 1.5,
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
      mb: 3,
    }}>
      {SCALAR_LAYOUT.map((group) => {
        const data = scalars[group.key];
        if (!data) return null;
        const items = group.items.filter(([k]) => data[k] != null);
        if (!items.length) return null;
        return (
          <Card key={group.key} variant="outlined" sx={{ borderColor: 'divider' }}>
            <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
              <Typography sx={{
                fontSize: '0.66rem', color: 'text.secondary',
                textTransform: 'uppercase', letterSpacing: '0.06em',
                fontWeight: 700, mb: 1,
              }}>
                {group.title}
              </Typography>
              <Stack spacing={0.6}>
                {items.map(([k, label, fmt]) => (
                  <Stack key={k} direction="row" justifyContent="space-between" spacing={1}>
                    <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
                      {label}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.78rem', fontFamily: 'monospace', fontWeight: 600,
                    }}>
                      {fmt(data[k])}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}

// ── Time-series table ───────────────────────────────────────────────────────

function TimeSeriesTable({ group, periods }) {
  const totalPeriods = periods.length;
  // Period window — full series can be 100+ columns; default to first 24
  // with toggles for last 24 / all. Keeps the page responsive.
  const [windowMode, setWindowMode] = useState(totalPeriods > 24 ? 'first24' : 'all');
  const visiblePeriods = useMemo(() => {
    if (windowMode === 'first24') return periods.slice(0, 24);
    if (windowMode === 'last24')  return periods.slice(-24);
    return periods;
  }, [periods, windowMode]);
  const visibleIdx = useMemo(() => {
    if (windowMode === 'first24') return [...Array(Math.min(24, totalPeriods)).keys()];
    if (windowMode === 'last24')  {
      const start = Math.max(0, totalPeriods - 24);
      return [...Array(totalPeriods - start).keys()].map((i) => start + i);
    }
    return [...Array(totalPeriods).keys()];
  }, [windowMode, totalPeriods]);

  return (
    <Card variant="outlined" sx={{ borderColor: 'divider', mb: 2 }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.5, pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
            {group.name}
          </Typography>
          <Chip size="small" label={group.unit} sx={{ height: 18, fontSize: '0.66rem' }} />
          <Chip size="small" label={group.frequency || ''} sx={{ height: 18, fontSize: '0.66rem' }} />
          <Box sx={{ flex: 1 }} />
          {totalPeriods > 24 && (
            <ToggleButtonGroup
              size="small" exclusive value={windowMode}
              onChange={(_, v) => v && setWindowMode(v)}
              sx={{ '& .MuiToggleButton-root': { py: 0.25, fontSize: '0.68rem' } }}
            >
              <ToggleButton value="first24">First 24</ToggleButton>
              <ToggleButton value="last24">Last 24</ToggleButton>
              <ToggleButton value="all">All ({totalPeriods})</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>
        <TableContainer component={Paper} variant="outlined"
                         sx={{ borderRadius: 0, borderLeft: 0, borderRight: 0,
                               borderBottom: 0, overflowX: 'auto', maxWidth: '100%' }}>
          <Table size="small" sx={{ tableLayout: 'auto' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{
                  position: 'sticky', left: 0, zIndex: 2,
                  bgcolor: 'background.paper',
                  fontWeight: 700, fontSize: '0.7rem',
                  borderRight: '1px solid', borderColor: 'divider', minWidth: 220,
                }}>
                  Line
                </TableCell>
                {visiblePeriods.map((p, i) => (
                  <TableCell key={i} align="right" sx={{
                    fontWeight: 700, fontSize: '0.66rem',
                    whiteSpace: 'nowrap', minWidth: 80,
                  }}>
                    {p.label || p.period_start}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {group.series.map((s, ri) => (
                <TableRow key={ri} sx={{
                  '& td': { borderBottom: '1px solid', borderColor: 'divider' },
                  bgcolor: s.highlight ? 'rgba(217,138,43,0.04)' : 'transparent',
                }}>
                  <TableCell sx={{
                    position: 'sticky', left: 0, zIndex: 1,
                    // Sticky cells MUST be opaque or scrolled cells bleed
                    // through. The 4%-amber highlight tint is layered over
                    // a solid paper backdrop so the tint shows but the
                    // numbers behind don't.
                    background: (theme) => s.highlight
                      ? `linear-gradient(rgba(217,138,43,0.04), rgba(217,138,43,0.04)), ${theme.palette.background.paper}`
                      : theme.palette.background.paper,
                    fontSize: '0.78rem', fontWeight: s.highlight ? 700 : 500,
                    borderRight: '1px solid', borderColor: 'divider',
                  }}>
                    {s.label}
                  </TableCell>
                  {visibleIdx.map((idx, j) => (
                    <TableCell key={j} align="right" sx={{
                      fontFamily: 'monospace', fontSize: '0.75rem',
                      fontWeight: s.highlight ? 700 : 400,
                      whiteSpace: 'nowrap',
                    }}>
                      {fmtByUnit(s.values[idx], group.unit)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

// ── Public component ───────────────────────────────────────────────────────

export default function ModelResultTables({ effective }) {
  if (!effective) return null;
  const groups = effective.time_series_groups || [];
  const periods = effective.period_grid || [];
  const frequency = effective.frequency || 'unknown';
  const scalars = effective.scalars;

  return (
    <Box>
      {scalars && (
        <>
          <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', mt: 1, mb: 1.5 }}>
            Model assumptions
          </Typography>
          <AssumptionCards scalars={scalars} />
        </>
      )}

      {groups.length > 0 && (
        <>
          <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mt: 1, mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
              Result tables
            </Typography>
            <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
              {periods.length} {frequency} periods{periods.length ? ` · ${periods[0].label} → ${periods[periods.length - 1].label}` : ''}
            </Typography>
          </Stack>
          {groups.map((g, i) => (
            <TimeSeriesTable key={i} group={g} periods={periods} />
          ))}
        </>
      )}
    </Box>
  );
}
